import {
	Box,
	Divider,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	tableCellClasses,
} from '@mui/material'
import moment from 'moment'
import { useState } from 'react'
import { COLORS } from 'styles/colors'
import CustomPagination from 'ui/CustomPagination'
import TableSkeleton from 'ui/skeleton/TableSkeleton'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
import { CardStandart } from '../../ui/CardStandart'
import { TypographyPrimary } from '../../ui/typography/TypographyPrimary'
import { TypographySecondary } from '../../ui/typography/TypographySecondary'

const columnPadding = {
	paddingTop: '14px',
	paddingBottom: '14px',
	paddingLeft: 0,
	paddingRight: '32px',
	verticalAlign: 'middle',
}

const tableSx = {
	tableLayout: 'fixed',
	width: '100%',
}

const historyChangePattern = /^(.*)\sс\s(.+)\sна\s(.+)$/

const HistoryChangeText = ({ text }: { text: string }) => {
	const match = text.match(historyChangePattern)
	if (!match) {
		return <TypographyPrimary>{text}</TypographyPrimary>
	}
	const [, prefix, previousValue, nextValue] = match
	return (
		<TypographyPrimary>
			{prefix}{' '}
			<Box
				component='span'
				sx={{
					color: COLORS.secondaryFont,
					textDecoration: 'line-through',
				}}
			>
				с {previousValue}
			</Box>{' '}
			на {nextValue}
		</TypographyPrimary>
	)
}

const formatHistoryDate = (value?: string) => {
	if (!value) return ''
	const parsed = moment(
		value,
		[
			'DD.MM.YY, HH:mm',
			'YYYY.MM.DD HH:mm:ss',
			'YYYY.MM.DD HH:mm',
			'YYYY.MM.DD',
			'YYYY-MM-DDTHH:mm:ssZ',
			'YYYY-MM-DDTHH:mm:ss',
			'YYYY-MM-DD HH:mm:ss',
			'YYYY-MM-DD HH:mm',
			'YYYY-MM-DD',
		],
		true,
	)
	const date = parsed.isValid() ? parsed : moment(value)
	if (!date.isValid()) return value
	const hasTime = /(?:T|\s)\d{1,2}:\d{2}/.test(value)
	return date.format(hasTime ? 'DD.MM.YY, HH:mm' : 'DD.MM.YY')
}

const ColumnGroup = () => (
	<colgroup>
		<col style={{ width: '220px' }} />
		<col style={{ width: '36%' }} />
		<col />
	</colgroup>
)

const rowsScrollSx = {
	flex: '1 1 auto',
	minHeight: 0,
	overflowY: 'auto',
	overflowX: 'hidden',
	'&::-webkit-scrollbar': {
		width: 4,
	},
	'&::-webkit-scrollbar-track': {
		backgroundColor: '#DBDBDB',
		borderRadius: 12,
		marginTop: '12px',
	},
	'&::-webkit-scrollbar-thumb': {
		backgroundColor: COLORS.icon,
		borderRadius: 12,
	},
}

const formatHistoryGroups = (groups, allGroups) => {
	const titles = (groups ?? []).map(group => group.group_title).filter(Boolean)
	if (!allGroups?.length || !groups?.length) return titles.join(', ')

	const coversAllGroups = allGroups.every(group =>
		groups.some(
			item =>
				item.group_key === group.groupkey || item.group_title === group.title,
		),
	)

	return coversAllGroups ? 'Все группы' : titles.join(', ')
}

const HistoryTable = props => {
	const { historyData, fetchindData, groupList = [] } = props
	const [visibleElements, setVisibleElements] = useState([])

	return (
		<CardStandart
			sx={{
				mt: '24px',
				position: 'relative',
				pt: '24px',
				pb: '16px',
				pl: '24px',
				pr: '8px',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					height: 'calc(100vh - 370px)',
					minHeight: 0,
				}}
			>
				<Box sx={{ flex: '0 0 auto', pr: '4px' }}>
					<Table sx={tableSx}>
						<ColumnGroup />
						<TableHead
							sx={{
								[`& .${tableCellClasses.root}`]: {
									backgroundColor: '#fff',
								},
							}}
						>
							<TableRow sx={{ width: '100%', height: '50px' }}>
								<TableCell
									align={'left'}
									padding={'none'}
									sx={{ ...columnPadding, width: '220px' }}
								>
									<TypographySecondary>Дата изменения</TypographySecondary>
								</TableCell>
								<TableCell
									align={'left'}
									padding={'none'}
									sx={{ ...columnPadding, width: '36%' }}
								>
									<TypographySecondary>Группы</TypographySecondary>
								</TableCell>
								<TableCell
									align={'left'}
									padding={'none'}
									sx={{ ...columnPadding, paddingRight: 0 }}
								>
									<TypographySecondary>Изменение</TypographySecondary>
								</TableCell>
							</TableRow>
						</TableHead>
					</Table>
				</Box>
				<Box sx={rowsScrollSx}>
					<Table sx={tableSx}>
						<ColumnGroup />
						{fetchindData ? (
							<TableBody>
								<TableCell colSpan={3} align={'center'} padding={'none'}>
									<TableSkeleton
										widthForBody={'100%'}
										heightForBody={'34px'}
										rowCount={36}
										totalHeight={'calc(100vh - 350px)'}
									/>
								</TableCell>
							</TableBody>
						) : visibleElements.length === 0 ? (
							<TableBody>
								<TableRow
									sx={{
										width: '100%',
										[`& .${tableCellClasses.root}`]: {
											borderBottom: 'none',
											height: '400px',
										},
									}}
								>
									<TableCell colSpan={3} align={'center'} padding={'none'}>
										<TypographySecondary14>
											Изменений не обнаружено
										</TypographySecondary14>
									</TableCell>
								</TableRow>
							</TableBody>
						) : (
							<TableBody>
								{visibleElements.map((data, index) => (
									<TableRow
										key={index}
										sx={{
											'&:hover': {
												background: COLORS.mainSecondaryOpacity,
											},
											[`& .${tableCellClasses.root}`]: {
												borderBottom: 'none',
											},
										}}
									>
										<TableCell
											align={'left'}
											padding={'none'}
											sx={{ ...columnPadding, width: '220px' }}
										>
											<TypographyPrimary>
												{formatHistoryDate(data.date)}
											</TypographyPrimary>
										</TableCell>
										<TableCell
											align={'left'}
											padding={'none'}
											sx={{ ...columnPadding, width: '36%' }}
										>
											<TypographyPrimary>
												{formatHistoryGroups(data.groups, groupList)}
											</TypographyPrimary>
										</TableCell>
										<TableCell
											align={'left'}
											padding={'none'}
											sx={{ ...columnPadding, paddingRight: 0 }}
										>
											{data.description.map((el, index) => (
												<HistoryChangeText key={index} text={el} />
											))}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						)}
					</Table>
				</Box>
			</Box>
			<Divider sx={{ mt: '16px', mr: '8px' }} />
			<Box sx={{ pt: '16px', pr: '8px' }}>
				<CustomPagination
					initialArray={historyData}
					setVisibleState={setVisibleElements}
					count={36}
				/>
			</Box>
		</CardStandart>
	)
}

export default HistoryTable
